import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Clock, Tag, ArrowRight, PenLine, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import SeoHead from '../components/SeoHead';
import NewsletterSignup from '../components/NewsletterSignup';
import api from '../utils/api';

export default function BlogListing() {
  const [posts, setPosts] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [activeTag, setActiveTag] = useState(null);
  const [generating, setGenerating] = useState(false);

  const fetchPosts = async (p = 1, tag = null) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: p, limit: 9 });
      if (tag) params.append('tag', tag);
      const { data } = await api.get(`/blog/posts?${params}`);
      setPosts(data.posts);
      setTotalPages(data.pages);
      setPage(data.page);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchTags = async () => {
    try {
      const { data } = await api.get('/blog/tags');
      setTags(data);
    } catch (e) { /* silent */ }
  };

  useEffect(() => { fetchPosts(1, activeTag); fetchTags(); }, [activeTag]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      await api.post('/blog/generate');
      await fetchPosts(1, activeTag);
      await fetchTags();
    } catch (e) { alert('Generation failed. Please try again.'); }
    finally { setGenerating(false); }
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const schema = {
    "@context": "https://schema.org",
    "@type": "Blog",
    name: "Writer's Blogck",
    description: "Expert insights on ghostwriting, book publishing, and overcoming writer's block. By AI Ghostwriter Pro.",
    url: "https://aighostwriterpro.com/blog",
    publisher: { "@type": "Organization", name: "AI Ghostwriter Pro", url: "https://aighostwriterpro.com" }
  };

  return (
    <>
      <SeoHead
        title="Writer's Blogck — Ghostwriting Tips, Book Publishing Guides & Author Resources"
        description="Expert articles on how to hire a ghostwriter, book publishing tips, overcoming writer's block, self-publishing on Amazon KDP, and the author journey. Free guides and industry insights from professional ghostwriters."
        keywords="ghostwriting blog, how to hire a ghostwriter, book publishing tips, writer's block solutions, self-publishing guide, Amazon KDP tips, ghostwriting industry, book writing advice, author resources, how to write a book"
        schemas={[schema]}
        path="/blog"
      />

      <section className="pt-32 pb-24 md:pt-40 md:pb-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20">
          {/* Header */}
          <div className="text-center mb-16">
            <p className="font-sans uppercase tracking-[0.2em] text-xs font-bold text-primary mb-4">The Blog</p>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-7xl leading-[0.9] tracking-tight mb-4" data-testid="blog-title">
              Writer's <span className="text-primary italic">Blogck</span>
            </h1>
            <p className="text-muted-foreground max-w-xl mx-auto leading-relaxed">
              Expert insights on ghostwriting, publishing, and the art of turning ideas into books. Because the only block we believe in is the one you build your career on.
            </p>
            <a href="/api/blog/rss" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 mt-3 text-xs text-primary hover:underline" data-testid="blog-rss-link">
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24"><circle cx="6.18" cy="17.82" r="2.18"/><path d="M4 4.44v2.83c7.03 0 12.73 5.7 12.73 12.73h2.83c0-8.59-6.97-15.56-15.56-15.56zm0 5.66v2.83c3.9 0 7.07 3.17 7.07 7.07h2.83c0-5.47-4.43-9.9-9.9-9.9z"/></svg>
              RSS Feed
            </a>
          </div>

          {/* Tags Filter */}
          {tags.length > 0 && (
            <div className="flex flex-wrap justify-center gap-2 mb-12" data-testid="blog-tags-filter">
              <button
                onClick={() => setActiveTag(null)}
                className={`px-4 py-1.5 rounded-sm text-xs font-semibold uppercase tracking-widest transition-all ${!activeTag ? 'bg-primary text-primary-foreground' : 'border border-white/10 text-muted-foreground hover:border-primary hover:text-primary'}`}
                data-testid="tag-all"
              >
                All Posts
              </button>
              {tags.slice(0, 8).map(t => (
                <button
                  key={t.tag}
                  onClick={() => setActiveTag(t.tag)}
                  className={`px-4 py-1.5 rounded-sm text-xs font-semibold uppercase tracking-widest transition-all ${activeTag === t.tag ? 'bg-primary text-primary-foreground' : 'border border-white/10 text-muted-foreground hover:border-primary hover:text-primary'}`}
                  data-testid={`tag-${t.tag}`}
                >
                  {t.tag} ({t.count})
                </button>
              ))}
            </div>
          )}

          {/* Loading */}
          {loading && (
            <div className="text-center py-20">
              <Loader2 className="w-8 h-8 text-primary animate-spin mx-auto" />
              <p className="text-muted-foreground mt-4 text-sm">Loading posts...</p>
            </div>
          )}

          {/* Empty State */}
          {!loading && posts.length === 0 && (
            <div className="text-center py-20 bg-white/5 border border-white/10 rounded-sm" data-testid="blog-empty">
              <PenLine className="w-12 h-12 text-primary mx-auto mb-4" strokeWidth={1.5} />
              <h3 className="font-serif text-2xl mb-3">No Posts Yet</h3>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">The blog is just getting started. Generate the first post to kick off Writer's Blogck!</p>
              <button
                onClick={handleGenerate}
                disabled={generating}
                data-testid="generate-first-post"
                className="inline-flex items-center gap-2 rounded-sm px-6 py-3 bg-primary text-primary-foreground font-semibold tracking-wide transition-all duration-300 shadow-[0_0_15px_rgba(212,175,55,0.3)] hover:bg-white hover:text-black"
              >
                {generating ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating...</> : <><PenLine className="w-4 h-4" /> Generate First Post</>}
              </button>
            </div>
          )}

          {/* Posts Grid */}
          {!loading && posts.length > 0 && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="blog-grid">
                {posts.map((post, i) => (
                  <article
                    key={post.id || i}
                    className="bg-white/5 border border-white/10 rounded-sm overflow-hidden hover:border-primary/30 transition-all duration-500 group flex flex-col"
                    data-testid={`blog-card-${i}`}
                  >
                    {/* Hero image with alt text */}
                    <div className="h-48 relative overflow-hidden bg-background">
                      {post.hero_image_url ? (
                        <img
                          src={post.hero_image_url}
                          alt={post.hero_image_alt || post.image_alt_1 || `${post.title} — ghostwriting tips and book publishing advice for authors`}
                          className="w-full h-full object-cover opacity-70 group-hover:opacity-90 transition-opacity duration-500 group-hover:scale-105 transition-transform"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-primary/10 via-background to-primary/5 flex items-center justify-center">
                          <PenLine className="w-10 h-10 text-primary/30" strokeWidth={1} />
                        </div>
                      )}
                      {!post.hero_image_url && post.image_alt_1 && <span className="sr-only" role="img" aria-label={post.image_alt_1}>{post.image_alt_1}</span>}
                    </div>

                    <div className="p-6 flex flex-col flex-1">
                      {/* Tags */}
                      {post.tags && post.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mb-3">
                          {post.tags.slice(0, 3).map(tag => (
                            <span key={tag} className="text-[10px] uppercase tracking-widest text-primary font-bold">{tag}</span>
                          ))}
                        </div>
                      )}

                      <h2 className="font-serif text-xl leading-snug mb-3 text-foreground group-hover:text-primary transition-colors">
                        <Link to={`/blog/${post.slug}`} data-testid={`blog-link-${i}`}>
                          {post.title}
                        </Link>
                      </h2>

                      <p className="text-sm text-muted-foreground leading-relaxed mb-4 flex-1">
                        {post.excerpt || post.meta_description}
                      </p>

                      <div className="flex items-center justify-between mt-auto pt-4 border-t border-white/5">
                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          <span>{formatDate(post.published_at)}</span>
                          {post.estimated_read_time && (
                            <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{post.estimated_read_time}</span>
                          )}
                        </div>
                        <Link
                          to={`/blog/${post.slug}`}
                          className="text-primary text-xs font-semibold uppercase tracking-widest flex items-center gap-1 hover:gap-2 transition-all"
                          data-testid={`read-more-${i}`}
                        >
                          Read <ArrowRight className="w-3 h-3" />
                        </Link>
                      </div>
                    </div>
                  </article>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center items-center gap-4 mt-12" data-testid="blog-pagination">
                  <button
                    onClick={() => fetchPosts(page - 1, activeTag)}
                    disabled={page <= 1}
                    className="px-4 py-2 rounded-sm border border-white/10 text-muted-foreground hover:border-primary hover:text-primary disabled:opacity-30 disabled:cursor-not-allowed transition-all flex items-center gap-1"
                  >
                    <ChevronLeft className="w-4 h-4" /> Prev
                  </button>
                  <span className="text-sm text-muted-foreground">Page {page} of {totalPages}</span>
                  <button
                    onClick={() => fetchPosts(page + 1, activeTag)}
                    disabled={page >= totalPages}
                    className="px-4 py-2 rounded-sm border border-white/10 text-muted-foreground hover:border-primary hover:text-primary disabled:opacity-30 disabled:cursor-not-allowed transition-all flex items-center gap-1"
                  >
                    Next <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              )}

              {/* Generate More */}
              <div className="text-center mt-12">
                <button
                  onClick={handleGenerate}
                  disabled={generating}
                  data-testid="generate-new-post"
                  className="inline-flex items-center gap-2 rounded-sm px-6 py-3 border border-white/20 text-foreground font-semibold tracking-wide hover:border-primary hover:text-primary transition-all"
                >
                  {generating ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating...</> : <><PenLine className="w-4 h-4" /> Generate New Post</>}
                </button>
              </div>

              {/* Newsletter Signup */}
              <div className="mt-20 max-w-lg mx-auto">
                <NewsletterSignup source="blog-listing" />
              </div>
            </>
          )}
        </div>
      </section>
    </>
  );
}
